import React, { useCallback, useEffect, useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { ScrollText, Download, Eye } from "lucide-react";
import { AdminTabs } from "@/components/AdminTabs";
import { Pagination } from "@/components/Pagination";
import { DateRangeFilter } from "@/components/DateRangeFilter";
import { downloadCSV } from "@/lib/csv";

const fmt = (s) => (s ? new Date(s).toLocaleString() : "");

export default function AdminAuditLogs() {
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const pageSize = 30;
  const [q, setQ] = useState("");
  const [action, setAction] = useState("all");
  const [actions, setActions] = useState([]);
  const [piiOnly, setPiiOnly] = useState(false);
  const [range, setRange] = useState({ start: "", end: "" });
  const [loading, setLoading] = useState(true);

  const load = useCallback(async (p = 1, rg = range, act = action, query = q, pii = piiOnly) => {
    setLoading(true);
    try {
      const params = { page: p, page_size: pageSize };
      if (query.trim()) params.q = query.trim();
      if (pii) params.pii = true;
      else if (act !== "all") params.action = act;
      if (rg.start) params.start = rg.start;
      if (rg.end) params.end = rg.end;
      const { data } = await api.get("/admin/audit-logs", { params });
      setItems(data.items); setTotal(data.total); setPage(data.page);
      if (data.actions) setActions(data.actions);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally { setLoading(false); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => { load(1, range, action, q, piiOnly); /* eslint-disable-next-line */ }, [range, action, piiOnly]);

  const exportCSV = async () => {
    try {
      const params = { export: true };
      if (q.trim()) params.q = q.trim();
      if (piiOnly) params.pii = true;
      else if (action !== "all") params.action = action;
      if (range.start) params.start = range.start;
      if (range.end) params.end = range.end;
      const { data } = await api.get("/admin/audit-logs", { params });
      const rows = (data.items || []).map((a) => ({
        time: fmt(a.created_at), actor: a.actor_username, role: a.actor_role,
        action: a.action, target_type: a.target_type, target_id: a.target_id, detail: a.detail,
      }));
      if (!rows.length) { toast.error("Nothing to export"); return; }
      downloadCSV("audit-logs.csv", rows);
      toast.success("CSV downloaded");
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message); }
  };

  return (
    <div className="container-x py-12" data-testid="admin-audit">
      <AdminTabs />
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1 className="font-heading text-3xl text-[#1E3F33] font-semibold flex items-center gap-2">
            <ScrollText className="w-6 h-6 text-[#2D6A4F]" /> Audit log ({total})
          </h1>
          <p className="text-[#5C6B64] mt-2">Immutable record of security-sensitive actions: logins, password changes, verifications, suspensions, complaint decisions, and <span className="text-[#CC5A3A] font-medium">PII access</span> (which admin viewed or exported customer/collector contact details and reports).</p>
        </div>
        <button data-testid="admin-audit-export-csv" onClick={exportCSV} disabled={!items.length} className="btn-secondary inline-flex items-center gap-2 text-sm py-2 px-3 disabled:opacity-50">
          <Download className="w-4 h-4" /> Export CSV
        </button>
      </div>

      <form onSubmit={(e) => { e.preventDefault(); load(1, range, action, q, piiOnly); }} className="mt-6 flex flex-wrap items-center gap-2">
        <input data-testid="admin-audit-search" className="input-soft max-w-xs" placeholder="Search actor, target, detail…" value={q} onChange={(e) => setQ(e.target.value)} />
        <select data-testid="admin-audit-action-filter" disabled={piiOnly} value={action} onChange={(e) => setAction(e.target.value)} className="rounded-lg border border-[#1E3F33]/15 bg-white px-3 py-2.5 text-sm text-[#1E3F33] focus:outline-none focus:border-[#2D6A4F] disabled:opacity-50">
          <option value="all">All actions</option>
          {actions.map((a) => <option key={a} value={a}>{a}</option>)}
        </select>
        <button
          type="button"
          data-testid="admin-audit-pii-toggle"
          onClick={() => setPiiOnly((v) => !v)}
          className={`inline-flex items-center gap-1.5 rounded-full px-3 py-2 text-sm font-medium border transition-colors ${
            piiOnly
              ? "bg-[#CC5A3A] text-white border-[#CC5A3A]"
              : "bg-white text-[#CC5A3A] border-[#CC5A3A]/40 hover:border-[#CC5A3A]"
          }`}
        >
          <Eye className="w-4 h-4" /> PII access
        </button>
        <button className="btn-primary py-2.5 px-4">Search</button>
      </form>
      <div className="mt-4"><DateRangeFilter onChange={(start, end) => setRange({ start, end })} /></div>

      {loading ? (
        <div className="mt-10 text-[#5C6B64]">Loading…</div>
      ) : items.length === 0 ? (
        <p className="mt-8 text-sm text-[#8A9A86]" data-testid="admin-audit-empty">No audit entries found.</p>
      ) : (
        <div className="mt-8 overflow-x-auto rounded-xl border border-[#1E3F33]/10">
          <table className="w-full text-sm" data-testid="admin-audit-table">
            <thead className="bg-[#F0EFEA] text-left text-[#1E3F33]"><tr>
              <th className="px-4 py-2.5 font-medium">Time</th>
              <th className="px-4 py-2.5 font-medium">Actor</th>
              <th className="px-4 py-2.5 font-medium">Action</th>
              <th className="px-4 py-2.5 font-medium">Target</th>
              <th className="px-4 py-2.5 font-medium">Detail</th>
            </tr></thead>
            <tbody>
              {items.map((a) => (
                <tr key={a.id} className="border-t border-[#1E3F33]/8" data-testid={`admin-audit-row-${a.id}`}>
                  <td className="px-4 py-2.5 text-[#5C6B64] whitespace-nowrap text-xs">{fmt(a.created_at)}</td>
                  <td className="px-4 py-2.5"><span className="text-[#1E3F33] font-medium">{a.actor_username}</span> <span className="text-[10px] text-[#8A9A86]">{a.actor_role}</span></td>
                  <td className="px-4 py-2.5">{a.action?.startsWith("pii.")
                    ? <span className="text-[10px] px-2 py-0.5 rounded-full bg-[#CC5A3A]/12 text-[#CC5A3A] font-medium inline-flex items-center gap-1"><Eye className="w-3 h-3" />{a.action}</span>
                    : <span className="text-[10px] px-2 py-0.5 rounded-full bg-[#1E3F33]/8 text-[#1E3F33]">{a.action}</span>}</td>
                  <td className="px-4 py-2.5 text-[#5C6B64] text-xs">{a.target_type}{a.target_id ? ` · ${a.target_id.slice(-6)}` : ""}</td>
                  <td className="px-4 py-2.5 text-[#5C6B64] text-xs">{a.detail}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      <Pagination page={page} total={total} pageSize={pageSize} onPage={(p) => load(p, range, action, q, piiOnly)} testid="admin-audit-pagination" />
    </div>
  );
}
